import React, { useState, useEffect, useCallback } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Stack,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  SelectChangeEvent,
} from '@mui/material';
import AppDialog from '../common/AppDialog';
import { Item, AdjustStockRequest, InventoryBatch, PriceEntryMode } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { apiClient } from '../../services/api';
import { errorMessage } from '../../utils/errorMessage';
import { tracksBatches } from '../../utils/inventoryMode';
import { getPriceForEntryMode } from '../../utils/pricing';

interface AdjustStockDialogProps {
  open: boolean;
  onClose: () => void;
  item: Item;
  batch?: InventoryBatch | null;
  onSuccess: () => void | Promise<void>;
  batchTrackingEnabled?: boolean;
}

const AdjustStockDialog: React.FC<AdjustStockDialogProps> = ({
  open,
  onClose,
  item,
  batch,
  onSuccess,
  batchTrackingEnabled,
}) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [batches, setBatches] = useState<InventoryBatch[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const canUseBatchTracking = batchTrackingEnabled !== false;

  const [formData, setFormData] = useState({
    newQuantity: batch ? batch.remainingQuantity : item.totalStockQuantity,
    newPrice: getPriceForEntryMode(
      batch?.price,
      batch?.priceGross,
      batch?.priceEntryMode ?? item.priceEntryMode,
    ) ?? getPriceForEntryMode(item.price, item.priceGross, item.priceEntryMode),
    notes: '',
    batchId: batch?.id || undefined as string | undefined,
    batchNumber: batch?.batchNumber || '',
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleInputChange = (field: string) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'newQuantity' ? value : value,
    }));
  };

  // Update form data when batch changes
  const loadBatches = useCallback(async () => {
    if (!item) return;
    setBatchesLoading(true);
    try {
      const batchesData = await apiClient.getInventoryBatches(item.id);
      setBatches(batchesData);
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to load batches'));
    } finally {
      setBatchesLoading(false);
    }
  }, [item]);

  useEffect(() => {
    if (open) {
      setFormData({
        newQuantity: batch ? batch.remainingQuantity : item.totalStockQuantity,
        newPrice: getPriceForEntryMode(
          batch?.price,
          batch?.priceGross,
          batch?.priceEntryMode ?? item.priceEntryMode,
        ) ?? getPriceForEntryMode(item.price, item.priceGross, item.priceEntryMode),
        notes: '',
        batchId: batch?.id || undefined,
        batchNumber: batch?.batchNumber || '',
      });
      setShowDeleteConfirm(false);
    }
  }, [open, batch, item]);

  // Load batches when dialog opens and item has batch tracking
  useEffect(() => {
    if (open && canUseBatchTracking && tracksBatches(item) && !batch) {
      loadBatches();
    }
  }, [canUseBatchTracking, open, item, batch, loadBatches]);

  const handleBatchChange = (event: SelectChangeEvent<string>) => {
    const batchId = event.target.value;
    const selectedBatch = batchId ? batches.find(b => b.id === batchId) : null;
    setFormData(prev => ({
      ...prev,
      batchId: batchId || undefined,
      newQuantity: selectedBatch ? selectedBatch.remainingQuantity : item.totalStockQuantity,
      newPrice: getPriceForEntryMode(
        selectedBatch?.price,
        selectedBatch?.priceGross,
        selectedBatch?.priceEntryMode ?? item.priceEntryMode,
      ) ?? getPriceForEntryMode(item.price, item.priceGross, item.priceEntryMode),
      batchNumber: selectedBatch?.batchNumber || '',
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    if (!Number.isInteger(Number(formData.newQuantity)) || Number(formData.newQuantity) < 0) {
      setError(t('inventory.newQuantity'));
      setLoading(false);
      return;
    }
    if (canUseBatchTracking && tracksBatches(item) && !formData.batchId) {
      setError(t('inventory.selectBatch'));
      setLoading(false);
      return;
    }
    if (canUseBatchTracking && tracksBatches(item) && !formData.batchNumber.trim()) {
      setError(t('inventory.batchNumberRequired'));
      setLoading(false);
      return;
    }

    try {
      const request: AdjustStockRequest = {
        newQuantity: Number(formData.newQuantity),
        ...(canUseBatchTracking && tracksBatches(item) && formData.batchId ? { newPrice: Number(formData.newPrice) } : {}),
        notes: formData.notes || undefined,
        batchId: formData.batchId,
        batchNumber: formData.batchNumber || undefined,
      };

      const response = await apiClient.adjustStock(item.id, request);
      
      if (response.success) {
        await onSuccess();
        onClose();
        setFormData({
          newQuantity: item.totalStockQuantity,
          newPrice: getPriceForEntryMode(item.price, item.priceGross, item.priceEntryMode),
          notes: '',
          batchId: undefined,
          batchNumber: '',
        });
      } else {
        setError(response.message || t('inventory.adjustStockError'));
      }
    } catch (err: unknown) {
      setError(errorMessage(err, t('inventory.adjustStockError')));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError(null);
    }
  };

  const handleDeleteBatch = async () => {
    if (!batch || !canUseBatchTracking) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.deleteBatch(batch.id);
      
      if (response.success) {
        setShowDeleteConfirm(false);
        await onSuccess();
        onClose();
      } else {
        // Translate specific error messages
        const errorMessage = response.message === 'CANNOT_DELETE_BATCH_WITH_STOCK'
          ? t('inventory.cannotDeleteBatchWithStock')
          : (response.message || t('inventory.deleteBatchError'));
        setError(errorMessage);
        setShowDeleteConfirm(false);
      }
    } catch (err: unknown) {
      const backendMessage = errorMessage(err, t('inventory.deleteBatchError'));

      const translatedMessage = backendMessage === 'CANNOT_DELETE_BATCH_WITH_STOCK'
        ? t('inventory.cannotDeleteBatchWithStock')
        : backendMessage;
      setError(translatedMessage);
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  // Get selected batch for validation
  const selectedBatch = canUseBatchTracking
    ? batch || (formData.batchId ? batches.find(b => b.id === formData.batchId) : null)
    : null;
  const currentQuantity = canUseBatchTracking && tracksBatches(item) && selectedBatch
    ? selectedBatch.remainingQuantity
    : item.totalStockQuantity;
  
  // Validation logic
  const isTrackedBatchSelected = canUseBatchTracking && tracksBatches(item) && Boolean(batch || formData.batchId);
  const isFormValid = Number.isInteger(Number(formData.newQuantity)) && String(formData.newQuantity).trim() !== '' && Number(formData.newQuantity) >= 0
    && (!(canUseBatchTracking && tracksBatches(item)) || batch || formData.batchId)
    && (!isTrackedBatchSelected
      || (Number.isFinite(Number(formData.newPrice)) && Number(formData.newPrice) >= 0));
  const priceEntryModeText = item.priceEntryMode === PriceEntryMode.VAT_INCLUDED
    ? t('inventory.priceEntryModeVatIncluded')
    : t('inventory.priceEntryModeVatExcluded');
  

  return (
      <AppDialog presentation="compact"
      open={open}
      onClose={handleClose}
      closeDisabled={loading}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: 600,
          m: { xs: 1, sm: 2 },
          maxHeight: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        },
      }}
    >
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        <DialogTitle>{t('inventory.adjustStock')}</DialogTitle>

        <DialogContent sx={{ width: '100%', boxSizing: 'border-box', flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: batch ? .5 : 2 }}>{item.name}</Typography>
            {batch && canUseBatchTracking && <Typography variant="body2" color="text.secondary" sx={{ mb: 2, overflowWrap: 'anywhere' }}>
              {t('inventory.batchNumber')}: {batch.batchNumber || t('inventory.noBatchNumber')}
            </Typography>}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Stack spacing={2} sx={{ width: '100%', minWidth: 0 }}>
              {/* Show batch selector only if no batch is pre-selected */}
              {canUseBatchTracking && tracksBatches(item) && !batch && (
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {t('inventory.selectBatch')}
                  </InputLabel>
                  <Select
                    value={formData.batchId || ''}
                    onChange={handleBatchChange}
                    label={t('inventory.selectBatch')}
                    disabled={batchesLoading}
                    required
                    sx={{ '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
                  >
                    {batches.map((b) => (
                      <MenuItem key={b.id} value={b.id}>
                        <Box display="flex" alignItems="center" gap={1} width="100%">
                          <Typography variant="body2">
                            {b.batchNumber || t('inventory.noBatchNumber')}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${b.remainingQuantity} ${t('inventory.units')}`}
                            color={b.expired ? 'error' : b.expiringSoon ? 'warning' : 'default'}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {batchesLoading && (
                    <Typography variant="caption" color="text.secondary">
                      {t('common.loading')}
                    </Typography>
                  )}
                </FormControl>
              )}

              {/* Batch Number (editable when batch is selected) */}
              {batch && canUseBatchTracking && (
                <Box sx={{ width: '100%', minWidth: 0, pt: 1 }}>
                  <TextField
                    label={t('inventory.batchSalePrice')}
                    type="number"
                    value={formData.newPrice ?? ''}
                    onChange={handleInputChange('newPrice')}
                    fullWidth
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">TRY</InputAdornment>,
                    }}
                    helperText={`${t('inventory.batchPriceEditHelp')} ${t('inventory.priceEntryModeHelp').replace('{mode}', priceEntryModeText)}`}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label={t('inventory.batchNumber')}
                    value={formData.batchNumber}
                    onChange={handleInputChange('batchNumber')}
                    fullWidth
                    required={canUseBatchTracking && tracksBatches(item)}
                    error={canUseBatchTracking && tracksBatches(item) && !formData.batchNumber.trim()}
                    placeholder={t('inventory.enterBatchNumber')}
                    helperText={t('inventory.batchNumberHelpText')}
                    sx={{
                      width: '100%',
                      minWidth: 0,
                      '& .MuiInputBase-root': { width: '100%', minWidth: 0 },
                    }}
                  />
                </Box>
              )}

              {!batch && canUseBatchTracking && tracksBatches(item) && formData.batchId && (
                <TextField
                  label={t('inventory.batchSalePrice')}
                  type="number"
                  value={formData.newPrice ?? ''}
                  onChange={handleInputChange('newPrice')}
                  fullWidth
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">TRY</InputAdornment>,
                  }}
                  helperText={`${t('inventory.batchPriceEditHelp')} ${t('inventory.priceEntryModeHelp').replace('{mode}', priceEntryModeText)}`}
                />
              )}

              <TextField
                label={t('inventory.newQuantity')}
                type="number"
                value={formData.newQuantity}
                onChange={handleInputChange('newQuantity')}
                fullWidth
                required
                inputProps={{ min: 0, step: 1 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t('inventory.units')}</InputAdornment>,
                }}
                helperText={
                  selectedBatch
                    ? `${t('inventory.currentQuantity')} ${t('inventory.inBatch')}: ${selectedBatch.remainingQuantity} ${t('inventory.units')}`
                    : `${t('inventory.currentQuantity')}: ${currentQuantity} ${t('inventory.units')}`
                }
                sx={{ mt: 2 }}
              />


              <TextField
                label={t('inventory.notes')}
                value={formData.notes}
                onChange={handleInputChange('notes')}
                fullWidth
                multiline
                rows={2}
                placeholder={t('inventory.reasonForAdjustment')}
                sx={{ mt: 2 }}
              />
              <Box sx={{ p: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <Typography variant="subtitle2">{t('inventory.operationPreview')}</Typography>
                <Typography variant="body2" color="text.secondary">{t('inventory.currentQuantity')}: {currentQuantity} {t('inventory.units')}</Typography>
                <Typography variant="body2" fontWeight={700}>{t('inventory.newQuantity')}: {formData.newQuantity} {t('inventory.units')}</Typography>
              </Box>
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions sx={{ flexShrink: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Box>
              {batch && canUseBatchTracking && (
                <Button 
                  onClick={() => setShowDeleteConfirm(true)} 
                  disabled={loading}
                  color="error"
                  variant="outlined"
                >
                  {t('inventory.deleteBatch')}
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={handleClose} disabled={loading}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !isFormValid}
              >
                {loading ? t('common.processing') : t('inventory.adjustStock')}
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Box>

      {/* Delete Confirmation Dialog */}
      <AppDialog presentation="confirm" open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} maxWidth="xs">
        <DialogTitle>{t('inventory.deleteBatchConfirm')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('inventory.deleteBatchWarning')}
          </Alert>
          <Typography>{t('inventory.deleteBatchMessage')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteBatch} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </AppDialog>
    </AppDialog>
  );
};

export default AdjustStockDialog;
